import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ref, get, update } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { database as db } from "../FirebaseConfig";
import toast from "react-hot-toast";

export default function LearnBK() {
  const navigate = useNavigate();
  const location = useLocation();

  const auth = getAuth();
  const [userData, setUserData] = useState({ name: "", mobile: "", date: "", slot: "" });
  const [userEmail, setUserEmail] = useState(null);
  const [dates, setDates] = useState([]);
  const [user, setUser] = useState(null);

  const [slots, setSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [bookedSlot, setBookedSlot] = useState(null);

  useEffect(() => {
    const redirectPath =
      new URLSearchParams(location.search).get("redirect") || "/learn-beekeeping";

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setUserEmail(currentUser.email);
        checkUserAlreadyBooked(currentUser.email);
      } else {
        setUser(null);
        navigate(`/login?redirect=${redirectPath}`);
      }
      fetchDates();

    });

    return () => unsubscribe();
  }, [navigate]);

  
  const fetchDates = async () => {
    const dateRef = ref(db, "slots");
    try {
      const snapshot = await get(dateRef);
      if (snapshot.exists()) {
        const allDates = snapshot.val();
        console.log("Raw Dates from Firebase:", allDates); // Debugging
  
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0); // Normalize to midnight
  
        // Convert and filter future dates
        const futureDates = Object.keys(allDates).filter(dateString => {
          const [year, month, day] = dateString.split("-").map(Number);
          const dateObj = new Date(year, month - 1, day); // Convert string to Date object
          dateObj.setHours(0, 0, 0, 0); // Normalize to midnight
  
          console.log(`Checking Date: ${dateString} => ${dateObj} (Today: ${currentDate})`); // Debugging
          return dateObj >= currentDate;
        });
  
        console.log("Filtered Future Dates:", futureDates); // Debugging
  
        setDates(futureDates);
      } else {
        console.log("No dates found in Firebase");
        setDates([]);
      }
    } catch (error) {
      console.error("Error fetching dates:", error);
    } finally {
      setLoading(false);
    }
  };
  


  const checkUserAlreadyBooked = async (email) => {
    const dateRef = ref(db, "slots");
    try {
      const snapshot = await get(dateRef);
      if (snapshot.exists()) {
        const allSlots = snapshot.val();
        for (let date in allSlots) {
          for (let slotId in allSlots[date]) {
            if (allSlots[date][slotId].users?.includes(email)) {
              setBookedSlot({ date, slotName: allSlots[date][slotId].name });
              return true;
            }
          }
        }
      }
    } catch (error) {
      console.error("Error checking existing booking:", error);
    }
    return false;
  };

  const fetchSlotsForDate = async (selectedDate) => {
    if (!selectedDate) return;
    setUserData({ ...userData, slot: "", date: selectedDate });
  
    const slotRef = ref(db, `slots/${selectedDate}`);
    try {
      const snapshot = await get(slotRef);
      if (snapshot.exists()) {
        const allSlots = snapshot.val();
        const currentTime = new Date();
  
        let filteredSlots = allSlots;
  
        // If selected date is today, remove past time slots
        if (selectedDate === new Date().toISOString().split("T")[0]) {
          filteredSlots = Object.fromEntries(
            Object.entries(allSlots).filter(([key, slot]) => {
              const [hour, minute] = slot.name.split(" ")[0].split(":").map(Number);
              const isPM = slot.name.includes("PM") && hour !== 12;
              const slotTime = new Date(selectedDate);
              slotTime.setHours(isPM ? hour + 12 : hour, minute, 0); // Convert 12-hour format to 24-hour format
  
              console.log(`Checking Slot: ${slot.name} -> Time: ${slotTime} (Current: ${currentTime})`); // Debugging
  
              return slotTime >= currentTime; // Keep only future slots
            })
          );
        }
  
        setSlots(filteredSlots);
      } else {
        setSlots({});
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
    }
  };
  

  const handleChange = (e) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });

    if (e.target.name === "date") {
      fetchSlotsForDate(e.target.value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, mobile, date, slot } = userData;

    if (!name || !mobile || !date || !slot) {
      toast.error("Please fill in all details.");
      return;
    }

    if (bookedSlot) {
      toast.success(`You have already booked: ${bookedSlot.slotName} on ${bookedSlot.date}`);
      return;
    }

    const selectedSlot = slots[slot];
    if (selectedSlot.booked >= selectedSlot.limit) {
      toast.error("This slot is fully booked. Please select another slot.");
      return;
    }

    const slotRef = ref(db, `slots/${date}/${slot}`);

    try {
      await update(slotRef, {
        booked: selectedSlot.booked + 1,
        users: [...(selectedSlot.users || []), userEmail],
      });

      toast.success("Slot booked successfully!");
      fetchSlotsForDate(date);
      checkUserAlreadyBooked(user.email);

    } catch (error) {
      console.error("Error booking slot:", error);
      alert("Failed to book the slot.");
    }
  };

  return (
    <div className="max-w-lg mx-auto p-6 mt-32 bg-white shadow-lg rounded-md mt-20 mb-10 border border-orange-300">
      <h2 className="text-3xl font-bold text-[#f47d02] mb-4 text-center">Book a Slot</h2>

      {bookedSlot && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-900 p-4 mb-4">
          <p className="font-bold">You have already booked:</p>
          <p>{bookedSlot.slotName} on {bookedSlot.date}</p>
        </div>
      )}

      {loading ? (
        <p className="text-center text-gray-500">Loading dates...</p>
      )  : (
        <>
        {!bookedSlot ? (
        <form  onSubmit={handleSubmit} className="space-y-4 " >
          <div>
            <label className="block font-medium text-gray-700">Name:</label>
            <input
              type="text"
              name="name"
              value={userData.name}
              onChange={handleChange}
              className="w-full p-2 border rounded focus:ring-[#f47d02] focus:border-[#f47d02]"
              required
            />
          </div>

          <div>
            <label className="block font-medium text-gray-700">Mobile Number:</label>
            <input
              type="tel"
              name="mobile"
              value={userData.mobile}
              onChange={handleChange}
              className="w-full p-2 border rounded focus:ring-[#f47d02] focus:border-[#f47d02]"
              required
            />
          </div>

          <div>
            <label className="block font-medium text-gray-700">Email:</label>
            <input
              type="email"
              value={userEmail || ""}
              disabled
              className="w-full p-2 border rounded bg-gray-100"
            />
          </div>

          <div>
            <label className="block font-medium text-gray-700">Select Date:</label>
            <select
              name="date"
              value={userData.date}
              onChange={handleChange}
              className="w-full p-2 border rounded focus:ring-[#f47d02] focus:border-[#f47d02]"
              required
            >
              <option value="">Choose a date</option>
              {dates.map((date) => (
                <option key={date} value={date}>{date}</option>
              ))}
            </select>
          </div>

          {userData.date && (
            <div>
              <label className="block font-medium text-gray-700">Select Slot:</label>
              <select
                name="slot"
                value={userData.slot}
                onChange={handleChange}
                className="w-full p-2 border rounded focus:ring-[#f47d02] focus:border-[#f47d02]"
                required
              >
                <option value="">Choose a slot</option>
                {Object.entries(slots).map(([key, slot]) => (
                  <option key={key} value={key} disabled={slot.booked >= slot.limit}>
                    {slot.name} ({slot.booked}/{slot.limit})
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-[#f47d02] text-white p-3 rounded-lg shadow-lg hover:bg-orange-600 transition duration-200"
          >
            Book Slot
          </button>
        </form>):(<></>)
}
        </>

      )}
    </div>
  );
}
